/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/alt-text */
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { isPatientGateUnlocked } from "Helpers/PatientGate";
import ModalBasic from "Components/Modal/Modal";
import Table from "Components/Common/Table/Table";
import { EDIT_PATIENT_PATH, PATIENT_ACCESS_LOGS, BASE_PATIENT_PATH, PATIENT_PASSWORD_RESET_PATH, PATIENT_VERIFY_PATH, PATIENT_MEMBERSHIPS_PATH, PATIENT_FREE_VISITS_PATH } from "Constants/Views";
import { formatPatientStatus, patientFormatterList } from "Helpers/Patients";
import { usePatientContext } from "Context/Patient";
import { DROPDOWN_OPTIONS } from "Constants/DropDown";
import MainLayout from "Layouts/MainLayout/MainLayout";
import { GetPatientById, PatientActivate, PatientDeactivate } from "Services/API/Patients.service";
import { Spinner } from "Components/Common/LoadingSpinner/Spinner";
import { ReactComponent as ProfileImage } from "Assets/Profile.svg";
import { ReactComponent as DeactivateIcon } from "Assets/Deactivate.svg";
import { ReactComponent as ActivateIcon } from "Assets/Activate.svg";
import { ReactComponent as PasswordIcon } from "Assets/PatientPassword.svg";
import { ReactComponent as FilterIcon } from "Assets/Filter.svg";
import { FormattedDropdown } from "Components/Common/FormattedDropdown/FormattedDropDown";
import { ErrorPopUp } from "Components/Common/PopUp/PopUp";
import { PaginationBtns } from "Components/Common/PaginationBtns/PaginationBtns";
import { errorFormatter } from "Helpers/ErrorMsgHandler";
import { UserAuthenticationModal } from "./UserModal";
import { SearchPatientByFilters } from "Services/API/Search.service";
import { useParams, useLocation } from "react-router-dom";
import { PATIENT_SEARCH_STATE_KEY as SEARCH_STATE_KEY } from "Constants/PatientSearch";
import { Button } from "Components/Common/Button/Button";
import { RadioButton } from "Components/Common/RadioButton/RadioButton";
import { DatePicker } from "Components/Common/DatePicker/DatePicker";
import { ReactComponent as SadFaceIcon } from "Assets/SadFace.svg";
import "Colors.css";
import { formatDate } from "Helpers/DateFormat";
import ClassNames from "Helpers/Common";
import PasswordReset from "./PasswordReset";
import VerifyCodes from "./VerifyCodes";
// Moved here from the old top-level Membership section: both screens act on
// patient accounts (a plan a patient buys, a free visit granted to a patient
// by email), so they belong beside the other patient tools rather than in a
// nav slot of their own.
import { MembershipListing } from "Components/Membership/membershipListing";
import { FreeVisits } from "Components/Membership/freeVisits";
import { formatBirthDate } from "Helpers/DateFormat";

const TABLE_HEADINGS = ["Name", "State", "Date of Birth", "Actions ", " ", ""];
const AUTO = "auto";
const FIRST_NAME = "firstName";
const LAST_NAME = "lastName";
const FULL_NAME = "fullName";
const USERNAME = "username";
const PHONE_NUMBER = "phoneNumber";
const ID = "cognitoId";
const MONGO_ID = "_id";

const FILTERS_INIT_STATE = {
  auto: true,
  firstName: false,
  lastName: false,
  userName: false,
  phoneNumber: false,
  id: false,
};

const getAutoSearchCriteria = (query) => {
  const trimmedQuery = query.trim();
  const cognitoIdRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  // Mongo ObjectId: 24 hex chars, no dashes. Checked BEFORE the cognito UUID
  // test purely for readability — the two shapes cannot collide, since a UUID
  // is 36 characters with dashes.
  const mongoIdRegex = /^[0-9a-fA-F]{24}$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^(\+1)?\d{10}$/;
  if (emailRegex.test(trimmedQuery)) {
    return USERNAME;
  }

  if (phoneRegex.test(trimmedQuery)) {
    return PHONE_NUMBER;
  }
  if (mongoIdRegex.test(trimmedQuery)) {
    return MONGO_ID;
  }
  if (cognitoIdRegex.test(trimmedQuery)) {
    return ID;
  }
  // "mark brown" -> first name AND last name search
  if (/\s/.test(trimmedQuery)) {
    return FULL_NAME;
  }

  return FIRST_NAME;
};

// Search state is kept in sessionStorage (NOT the URL: queries are patient
// emails/phones/names — PHI that must not land in browser history or server
// access logs). Per-tab, cleared when the tab closes; Back from a patient
// detail page restores the search and its results on remount.

const readStoredSearchState = () => {
  const fallback = {
    q: "",
    page: 1,
    dobFrom: "",
    dobTo: "",
    filterBy: FILTERS_INIT_STATE,
  };
  try {
    const raw = sessionStorage.getItem(SEARCH_STATE_KEY);
    if (!raw) return fallback;
    const stored = JSON.parse(raw);
    const filter = stored?.filter;
    return {
      q: typeof stored?.q === "string" ? stored.q : "",
      page: Math.max(1, parseInt(stored?.page, 10) || 1),
      dobFrom: stored?.dobFrom || "",
      dobTo: stored?.dobTo || "",
      filterBy:
        filter && filter !== "auto" && filter in FILTERS_INIT_STATE
          ? { ...FILTERS_INIT_STATE, auto: false, [filter]: true }
          : FILTERS_INIT_STATE,
    };
  } catch {
    return fallback;
  }
};

export function PatientsMainPage() {
  const { patient } = useParams();
  const isPatientsTab = patient === ":patient";
  const isPasswordResetTab = patient === "password-reset";
  const isVerifyTab = patient === "verify";
  const isMembershipsTab = patient === "memberships";
  const isFreeVisitsTab = patient === "free-visits";
  // The two moved tabs show no patient records — a price list, and a form that
  // takes an email — so they keep the access they had as their own section and
  // do NOT sit behind the patient password gate. Putting them behind it would
  // be a new restriction smuggled in by a layout change.
  const isUngatedTab = isMembershipsTab || isFreeVisitsTab;
  // captured once on mount — restores search state after Back from a detail page
  const [initialUrlState] = useState(() => readStoredSearchState());

  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [pageNumber, setPageNumber] = useState(initialUrlState.page);
  // password gate: within the 24h unlock window (Helpers/PatientGate) the
  // modal is skipped entirely
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(
    () => !isPatientGateUnlocked() && !isUngatedTab,
  );
  const [showPatientsTable, setShowPatientsTable] = useState(() =>
    isPatientGateUnlocked(),
  );
  const [searchedPatientResult, setSearchesPatientResult] = useState({});
  const [countDown, setCountDown] = useState(0);
  const [selectedFilterBy, setSelectedFilterBy] = useState(
    initialUrlState.filterBy,
  );
  const [dobTo, setDobTo] = useState(initialUrlState.dobTo);
  const [dobFrom, setDobFrom] = useState(initialUrlState.dobFrom);
  const [searchQuery, setSearchQuery] = useState(initialUrlState.q);
  const [searchResults, setSearchResults] = useState([]);

  // Switching tabs does NOT remount this page (the route uses `render`, and
  // all five tabs match the same /patient/:patient route), so the modal's
  // initial state is only ever evaluated once. Without this, an admin who
  // landed on Memberships while locked could click Patients and get a blank
  // page: no modal, and showPatientsTable still false.
  useEffect(() => {
    if (isUngatedTab || isPatientGateUnlocked()) return;
    setIsAuthModalOpen(true);
  }, [isUngatedTab, patient]);

  // keep sessionStorage in sync with the current search state (never the
  // URL — the query is PHI)
  useEffect(() => {
    if (!isPatientsTab) return;
    try {
      const activeFilter = Object.keys(selectedFilterBy).find(
        (key) => key !== "auto" && selectedFilterBy[key],
      );
      sessionStorage.setItem(
        SEARCH_STATE_KEY,
        JSON.stringify({
          q: searchQuery.trim(),
          page: pageNumber,
          filter: activeFilter || "auto",
          dobFrom,
          dobTo,
        }),
      );
    } catch {
      // storage unavailable — search restore simply won't happen
    }
  }, [searchQuery, pageNumber, selectedFilterBy, dobFrom, dobTo, isPatientsTab]);

  const fetchPatients = () => {
    setLoading(true);
    SearchPatientByFilters(
      {
        // auto branch matters for the restored-from-URL fetch: without it a
        // restored query searched by LAST_NAME regardless of its shape
        searchCriteria: selectedFilterBy?.auto
          ? getAutoSearchCriteria(searchQuery)
          : selectedFilterBy?.userName
            ? USERNAME
            : selectedFilterBy?.phoneNumber
              ? PHONE_NUMBER
              : selectedFilterBy?.id
                ? ID
                : selectedFilterBy?.firstName
                  ? FIRST_NAME
                  : LAST_NAME,
        query: searchQuery.trim().toLowerCase(),
        dob_from: dobFrom ? dobFrom : null,
        dob_to: dobTo ? dobTo : null,
      },
      pageNumber,
    )
      .then((res) => {
        if (res) {
          setSearchResults([]);
          setPatients(patientFormatterList(res?.data));
          setHasNextPage(res?.pagination?.hasNext);
          setHasPreviousPage(res?.pagination?.hasPrevious);
          setLoading(false);
        } else if (!res) {
          setSearchResults([]);
          setPatients([]);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error("[SearchPatientByFilters][API_ERROR] - ", err);
      });

    return () => {
      setPatients([]);
    };
  };

  useEffect(() => {
    let timerId;

    if (showPatientsTable) {
      setCountDown(60 * 3);
      timerId = setInterval(() => {
        setCountDown((countDown) => countDown - 1);
      }, 1000);
    } else {
      clearInterval(timerId);
    }

    return () => clearInterval(timerId);
  }, [showPatientsTable]);
  useEffect(() => {
    if (showPatientsTable) {
      function handleKeyDown(e) {
        setCountDown(60 * 3);
      }

      document.addEventListener("keydown", handleKeyDown);

      // Don't forget to clean up
      return function cleanup() {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [showPatientsTable]);
  useEffect(() => {
    if (countDown === 0) {
      // inactivity relock only applies OUTSIDE the 24h unlock window
      // (per Omer 2026-08-16: ask for the password at most once every 24h)
      if (!isPatientGateUnlocked()) {
        setIsAuthModalOpen(true);
        setShowPatientsTable(false);
      }
    } else {
      var timer;
      var timeout = function () {};
      timer = setTimeout(timeout, 5000);
      window.onmousemove = function () {
        setCountDown(60 * 3);
        timer = setTimeout(timeout, 5000);
      };
    }
  }, [countDown]);
  useEffect(() => {
    if (showPatientsTable) {
      fetchPatients();
    }
  }, [pageNumber, showPatientsTable]);
  useEffect(() => {
    if (patient === "PatientDetail") {
      setIsAuthModalOpen(false);
      setShowPatientsTable(true);
    }
  }, [patient]);

  const HeaderProps = {
    setPatients,
    fetchPatients,
    setHasNextPage,
    searchedPatientResult,
    setSearchesPatientResult,
    setSearchResults,
    searchResults,
    setHasPreviousPage,
    dobTo,
    dobFrom,
    setDobTo,
    setDobFrom,
    selectedFilterBy,
    setSelectedFilterBy,
    searchQuery,
    setSearchQuery,
    pageNumber,
    setPageNumber,
    isPatientsTab,
    isPasswordResetTab,
    isVerifyTab,
    isMembershipsTab,
    isFreeVisitsTab,
  };
  // console.log("Patients are", patients);
  return (
    <MainLayout>
      <UserAuthenticationModal
        modalOpen={isAuthModalOpen}
        icon={<PasswordIcon />}
        btnText="Continue"
        heading="Re-enter Password"
        content="To access patient information, please enter your password"
        setShowPatientsTable={setShowPatientsTable}
        setIsAuthModalOpen={setIsAuthModalOpen}
      />
      {showPatientsTable && isPatientsTab && (
        <div className="p-8 flex flex-col gap-8">
          {apiError && <ErrorPopUp error={apiError} setApiError={setApiError} />}
          <Header {...HeaderProps} />
          {!loading && patients.length <= 0 ? (
            <div className="flex flex-col items-center justify-center h-96">
              <SadFaceIcon className="h-16 w-16  animate-bounce text-red-800" />
              <p className="mt-1 text-md font-medium animate-pulse text-red-800">Sorry no data found</p>
            </div>
          ) : (
            patients?.length > 0 && (
              <Table
                heading={<TableHeading />}
                body={
                  !loading &&
                  patients?.length > 0 && (
                    <TableBody
                      data={patients}
                      setApiError={setApiError}
                      fetchPatients={fetchPatients}
                      searchResults={searchedPatientResult}
                    />
                  )
                }
              />
            )
          )}

          {loading && <Spinner />}

          {!loading && patients.length > 0 && isPatientsTab && (
            <PaginationBtns
              isNextDisabled={!hasNextPage}
              isPrevDisabled={!hasPreviousPage}
              onNext={() => {
                if (hasNextPage) {
                  setPageNumber(pageNumber + 1);
                }
              }}
              onPrevious={() => {
                if (hasPreviousPage) {
                  setPageNumber(pageNumber - 1);
                }
              }}
            />
          )}
        </div>
      )}

      {showPatientsTable && isPasswordResetTab && (
        <div className="p-8 flex flex-col gap-8">
          <Header {...HeaderProps} />
          <PasswordReset />
        </div>
      )}

      {showPatientsTable && isVerifyTab && (
        <div className="p-8 flex flex-col gap-8">
          <Header {...HeaderProps} />
          <VerifyCodes />
        </div>
      )}

      {isMembershipsTab && (
        <div className="p-8 flex flex-col gap-8">
          <Header {...HeaderProps} />
          <MembershipListing />
        </div>
      )}

      {isFreeVisitsTab && (
        <div className="p-8 flex flex-col gap-8">
          <Header {...HeaderProps} />
          <FreeVisits />
        </div>
      )}
    </MainLayout>
  );
}
const Header = ({
  setPatients,
  fetchPatients,
  setHasNextPage,
  searchedPatientResult,
  setSearchesPatientResult,
  setSearchResults,
  searchResults,
  setHasPreviousPage,
  dobTo,
  dobFrom,
  setDobTo,
  setDobFrom,
  selectedFilterBy,
  setSelectedFilterBy,
  searchQuery,
  setSearchQuery,
  pageNumber,
  setPageNumber,
  isPatientsTab,
  isPasswordResetTab,
  isVerifyTab,
  isMembershipsTab,
  isFreeVisitsTab,
}) => {
  const location = useLocation();
  const [filterView, setFilterView] = useState(false);
  const [applyFilter, setApplyFilter] = useState(false);
  // `page` is explicit because setPageNumber is async: a fresh query must send
  // page 1 in THIS request, not whatever page the previous result set was on.
  // Submitting a new query from page 4 used to ask for page 4 of the new
  // results, which is usually empty and reads as "no matches".
  // `query` is explicit for the same reason `page` is: setSearchQuery is
  // async, so a caller that has just set a NEW query would otherwise search
  // the PREVIOUS one from this closure. The nav-bar search hits exactly that.
  const patientsSearch = (enterPressed = false, page = 1, query = null) => {
    const effectiveQuery = query !== null ? query : searchQuery;
    const searchCriteria = selectedFilterBy?.auto
      ? getAutoSearchCriteria(effectiveQuery)
      : selectedFilterBy?.userName
        ? USERNAME
        : selectedFilterBy?.phoneNumber
          ? PHONE_NUMBER
          : selectedFilterBy?.id
            ? ID
            : selectedFilterBy?.firstName
              ? FIRST_NAME
              : LAST_NAME;
    console.log(searchCriteria, "Criteria");
    SearchPatientByFilters(
      {
        searchCriteria,
        query: effectiveQuery.trim().toLowerCase(),
        dob_from: dobFrom ? dobFrom : null,
        dob_to: dobTo ? dobTo : null,
      },
      page,
    )
      .then((res) => {
        if (res && !enterPressed) {
          setSearchResults([...res?.data]);
          setPatients(patientFormatterList(res?.data));
          setHasNextPage(res?.pagination?.hasNext);
          setHasPreviousPage(res?.pagination?.hasPrevious);
        } else if (res && enterPressed) {
          setSearchResults([]);
          setPatients(patientFormatterList(res?.data));
          setHasNextPage(res?.pagination?.hasNext);
          setHasPreviousPage(res?.pagination?.hasPrevious);
        } else if (!res) {
          setSearchResults([]);
          setPatients([]);
        }
      })
      .catch((err) => {
        console.error("[SearchPatientByFilters][API_ERROR] - ", err);
      });
    return () => {
      setSearchResults([]);
      if (enterPressed) {
        setPatients([]);
      }
    };
  };

  // A search submitted from the global nav box. The query itself travels in
  // sessionStorage (it is PHI and must not touch the URL); the navigation
  // carries only a timestamp, which is what lets an ALREADY-MOUNTED page know
  // to re-read, and what makes the same term twice re-run.
  //
  // THE PASSWORD GATE IS PRESERVED BY THE isPatientsTab GUARDS BELOW. It used
  // to hold "by construction" — the Header only rendered once
  // showPatientsTable was true — but that stopped being true when Memberships
  // and Free Visits moved in: those two tabs render the Header without the
  // gate, deliberately, because neither shows patient records. Every effect
  // here that can reach the patient API therefore checks the tab itself. A
  // search arriving from another route while the gate is locked still waits:
  // the modal shows first, and the effect runs after the unlock, so the
  // pending query is honoured rather than dropped or slipped past the gate.
  const navSearchAt = location?.state?.patientSearch?.at;
  useEffect(() => {
    if (!isPatientsTab || !navSearchAt) return;
    const incoming = readStoredSearchState();
    setSearchQuery(incoming.q);
    setPageNumber(1);
    if (location?.state?.patientSearch?.openFilters) setFilterView(true);
    // Pass the query explicitly — setSearchQuery above has not applied yet.
    if (incoming.q.trim().length >= 2) patientsSearch(true, 1, incoming.q);
    // patientsSearch is redefined every render; depending on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navSearchAt, isPatientsTab]);

  // Skip the first run: the parent's [pageNumber, showPatientsTable] effect
  // already fetches on mount (now criteria-aware, including a search query
  // restored from the URL). Running here too caused a duplicate request —
  // and, on URL-restore, an unwanted open suggestions dropdown.
  const skipInitialSearchRun = useRef(true);
  useEffect(() => {
    if (skipInitialSearchRun.current) {
      skipInitialSearchRun.current = false;
      return;
    }
    // Only the Patients tab has a patient list to search. The other four
    // render this same Header for its tab strip and would otherwise fire a
    // patient query for a table that is not on screen.
    if (!isPatientsTab) return;
    if (searchQuery.trim().length >= 2 || dobTo || dobFrom) {
      // Changing the query changes the result set, so any page position from
      // the previous one is meaningless. patientsSearch() already requests
      // page 1 by default; this keeps the pagination controls agreeing with
      // what was actually fetched. Setting it to 1 when it is already 1 is a
      // no-op, so no extra request is triggered.
      setPageNumber(1);
      patientsSearch();
    } else if (searchQuery.trim().length === 0) {
      setPageNumber(1);
      fetchPatients();
    }
  }, [searchQuery, applyFilter, dobTo, dobFrom, isPatientsTab]);

  return (
    <div className="flex justify-between items-center ">
      <div className="flex items-center gap-6 mr-5">
        {" "}
        <span className="font-semibold text-2xl sapphire">Patients</span>
        <Link to={BASE_PATIENT_PATH}>
          <span
            className={ClassNames(
              "light_grayish_navy text-sm font-medium sapphire cursor-pointer text-center mt-1 py-2 px-2 rounded-md",
              isPatientsTab && "bg-gray-100 font-semibold text-black",
            )}
          >
            Patients
          </span>
        </Link>
        <Link to={PATIENT_PASSWORD_RESET_PATH}>
          <span
            className={ClassNames(
              "light_grayish_navy text-sm font-medium cursor-pointer mt-1 py-2 px-2 rounded-md",
              isPasswordResetTab ? "bg-gray-100 font-semibold text-black" : "",
            )}
          >
            Password Reset
          </span>
        </Link>
        <Link to={PATIENT_VERIFY_PATH}>
          <span
            className={ClassNames(
              "light_grayish_navy text-sm font-medium cursor-pointer mt-1 py-2 px-2 rounded-md",
              isVerifyTab ? "bg-gray-100 font-semibold text-black" : "",
            )}
          >
            Verification Codes
          </span>
        </Link>
        <Link to={PATIENT_MEMBERSHIPS_PATH}>
          <span
            className={ClassNames(
              "light_grayish_navy text-sm font-medium cursor-pointer mt-1 py-2 px-2 rounded-md",
              isMembershipsTab ? "bg-gray-100 font-semibold text-black" : "",
            )}
          >
            Memberships
          </span>
        </Link>
        <Link to={PATIENT_FREE_VISITS_PATH}>
          <span
            className={ClassNames(
              "light_grayish_navy text-sm font-medium cursor-pointer mt-1 py-2 px-2 rounded-md",
              isFreeVisitsTab ? "bg-gray-100 font-semibold text-black" : "",
            )}
          >
            Free Visits
          </span>
        </Link>
      </div>
      {isPatientsTab && (
        <div className="flex items-center justify-end gap-3 w-3/3 relative">
          {/* The patient search box now lives in the top nav
              (Components/Navbar/GlobalPatientSearch) so it works from every
              route. This page still owns the search STATE and the results —
              the nav only hands it a query via sessionStorage and a
              location-state ping. Filters stay here because FilterView reads
              dobFrom/dobTo/selectedFilterBy, which live in this component. */}
          <Button
            variant="transparent"
            className="bg-white text-base font-normal sapphire border border-gray-200 py-2 px-4 inline-flex rounded-md w-28 h-8 relative"
            btnText="Filters"
            isDisabled={false}
            onClick={() => setFilterView((prevState) => !prevState)}
          >
            <FilterIcon className="h-5 w-5 text-black" />
          </Button>
          {filterView && (
            <FilterView
              dobTo={dobTo}
              dobFrom={dobFrom}
              setDobTo={setDobTo}
              setDobFrom={setDobFrom}
              searchQuery={searchQuery}
              setFilterView={setFilterView}
              setApplyFilter={setApplyFilter}
              selectedFilterBy={selectedFilterBy}
              setSelectedFilterBy={setSelectedFilterBy}
              setPageNumber={setPageNumber}
            />
          )}
        </div>
      )}
    </div>
  );
};

const TableHeading = () => {
  return TABLE_HEADINGS.map((head, index) => (
    <th key={index} scope="col" className="bali_hai px-6 py-3 text-left text-sm font-medium">
      {head}
    </th>
  ));
};

const TableBody = ({ data, setApiError, fetchPatients, searchResults }) => {
  const { setActivePatient, setActiveView, setGeneralInfo } = usePatientContext();
  const [selectedOption, setSelectedOption] = useState("");
  const [selectedUserEmail, setSelectedUserEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [modal, setModal] = useState(false);

  const setPatientView = (id) => {
    setActiveView(EDIT_PATIENT_PATH.replace(":id", id));
  };

  return (
    <>
      {!searchResults?.id
        ? data?.map((patient, index) => (
            <tr key={index}>
              <td
                className="px-6 py-4 whitespace-nowrap"
                onClick={() => {
                  setActivePatient({
                    name: patient?.firstName + " " + patient?.lastName,
                    id: patient.id,
                  });
                  setGeneralInfo(patient);
                  setPatientView(patient.id);
                }}
              >
                <div className="flex items-center">
                  <span className="inline-block h-10 w-10 rounded-full overflow-hidden bg-gray-100 cursor-pointer">
                    {patient.image ? <img className="h-10 w-10 rounded-full" src={patient.image} /> : <ProfileImage />}
                  </span>
                  <div className="ml-4">
                    <span className="text-sm font-medium eclipse cursor-pointer">
                      {patient.firstName} {patient.lastName}
                    </span>
                    <br />
                    <span className="bali_hai text-sm cursor-pointer">{patient.userName}</span>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm bali_hai">{patient.state}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm bali_hai">{patient.DOB}</td>

              <td className="px-6 py-4 whitespace-nowrap text-sm w-64 text-gray-500">
                <FormattedDropdown
                  status={patient.actionStatus}
                  dropDownOptions={DROPDOWN_OPTIONS}
                  onClick={(selectedOption) => {
                    setSelectedUserEmail(patient.userName);
                    setModal(true);
                    setSelectedOption(selectedOption);
                  }}
                />
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <span
                  onClick={() => {
                    setActivePatient({
                      name: patient?.firstName + " " + patient?.lastName,
                      id: patient.id,
                    });
                    setGeneralInfo(patient);
                    setPatientView(patient.id);
                  }}
                  className="text-blue-chill hover:text-blue-light cursor-pointer"
                >
                  Edit
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <span
                  onClick={() => {
                    setActivePatient({
                      name: patient?.firstName + " " + patient?.lastName,
                      id: patient.id,
                    });

                    setActiveView(PATIENT_ACCESS_LOGS);
                  }}
                  className="text-blue-chill hover:text-blue-light cursor-pointer"
                >
                  View Access Log
                </span>
              </td>
            </tr>
          ))
        : searchResults?.id && (
            <tr key={searchResults?.id}>
              <td
                className="px-6 py-4 whitespace-nowrap"
                onClick={() => {
                  setActivePatient({
                    name: searchResults?.firstName + " " + searchResults?.lastName,
                    id: searchResults?.id,
                  });
                  setGeneralInfo(searchResults);
                  setPatientView(searchResults?.id);
                }}
              >
                <div className="flex items-center">
                  <span className="inline-block h-10 w-10 rounded-full overflow-hidden bg-gray-100 cursor-pointer">
                    {searchResults?.image ? <img className="h-10 w-10 rounded-full" src={searchResults?.image} /> : <ProfileImage />}
                  </span>
                  <div className="ml-4">
                    <span className="text-sm font-medium eclipse cursor-pointer">
                      {`${searchResults?.firstName} ${searchResults?.lastName}`}
                    </span>
                    <br />
                    <span className="bali_hai text-sm cursor-pointer">{searchResults?.userName}</span>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm bali_hai">{searchResults?.state}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm bali_hai">{searchResults?.DOB}</td>

              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                <FormattedDropdown
                  status={searchResults?.actionStatus}
                  dropDownOptions={DROPDOWN_OPTIONS}
                  onClick={(selectedOption) => {
                    setSelectedUserEmail(searchResults?.userName);
                    setModal(true);
                    setSelectedOption(selectedOption);
                  }}
                />
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <span
                  onClick={() => {
                    setActivePatient({
                      name: searchResults?.firstName + " " + searchResults?.lastName,
                      id: searchResults?.id,
                    });
                    setGeneralInfo(searchResults);
                    setPatientView(searchResults?.id);
                  }}
                  className="text-blue-chill hover:text-blue-light cursor-pointer"
                >
                  Edit
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <span
                  onClick={() => {
                    setActivePatient({
                      name: searchResults?.firstName + " " + searchResults?.lastName,
                      id: searchResults?.id,
                    });

                    setActiveView(PATIENT_ACCESS_LOGS);
                  }}
                  className="text-blue-chill hover:text-blue-light cursor-pointer"
                >
                  View Access Log
                </span>
              </td>
            </tr>
          )}
      {modal && selectedOption === "Deactivate" ? (
        <ModalBasic
          open={modal}
          icon={<DeactivateIcon />}
          btnText="Deactivate"
          loading={isLoading}
          heading="Deactivate Profile"
          content="Are you sure you want to deactivate this profile?"
          //TODO Content confirmation with UX
          setOpen={(value) => {
            if (value === "Deactivate") {
              setIsLoading(true);
              PatientDeactivate({ email: selectedUserEmail })
                .then((res) => {
                  setSelectedOption("");
                  setIsLoading(false);
                  setModal(false);
                  fetchPatients();
                })
                .catch((err) => {
                  setSelectedOption("");
                  setIsLoading(false);
                  setModal(false);
                  setApiError(err?.response?.data?.errors);
                  console.error("[PatientDeactivate][API_ERROR] - ", err);
                });
            } else {
              setModal(false);
            }
          }}
        />
      ) : selectedOption === "Activate" ? (
        <ModalBasic
          open={modal}
          icon={<ActivateIcon />}
          btnText="Activate"
          loading={isLoading}
          heading="Activate Profile"
          content="Are you sure you want to Activate this profile?"
          //TODO Content confirmation with UX
          setOpen={(value) => {
            if (value === "Activate") {
              setIsLoading(true);
              PatientActivate({ email: selectedUserEmail })
                .then((res) => {
                  setSelectedOption("");
                  setIsLoading(false);
                  setModal(false);
                  fetchPatients();
                })
                .catch((err) => {
                  setSelectedOption("");
                  setIsLoading(false);
                  setModal(false);
                  setApiError(err?.response?.data?.errors);
                  console.error("[PatientActivate][API_ERROR] - ", err);
                });
            } else {
              setModal(false);
            }
          }}
        />
      ) : (
        ""
      )}
    </>
  );
};

const FilterView = ({
  dobTo,
  dobFrom,
  setDobFrom,
  setDobTo,
  searchQuery,
  setFilterView,
  setApplyFilter,
  selectedFilterBy,
  setSelectedFilterBy,
  setPageNumber,
}) => {
  return (
    <div className="flex flex-col p-2 gap-2 w-96 bg-white absolute right-0 top-full mt-2 z-50 shadow-lg rounded-md">
      <fieldset className="mt-4 space-y-6">
        <div className="space-y-2">
          <RadioButton
            id="1"
            label="Auto"
            className="text-sm font-medium"
            value={AUTO}
            isSelected={!!selectedFilterBy?.auto}
            isDisabled={!!/^[0-9]+$/.test(searchQuery)}
            onChange={() => {
              setSelectedFilterBy({ ...FILTERS_INIT_STATE, auto: true });
            }}
          />
          <RadioButton
            id="2"
            label="First Name"
            className="text-sm font-medium"
            value={FIRST_NAME}
            isSelected={!!selectedFilterBy?.firstName}
            isDisabled={!!/^[0-9]+$/.test(searchQuery)}
            onChange={() => {
              setSelectedFilterBy({ ...FILTERS_INIT_STATE, auto: false, firstName: true });
            }}
          />
          <RadioButton
            id="2"
            label="Last Name"
            className="text-sm font-medium"
            value={LAST_NAME}
            isSelected={!!selectedFilterBy?.lastName}
            isDisabled={!!/^[0-9]+$/.test(searchQuery)}
            onChange={() => {
              setSelectedFilterBy({
                ...FILTERS_INIT_STATE,
                auto: false,
                firstName: false,
                lastName: true,
              });
            }}
          />
          <RadioButton
            id="3"
            label="Email"
            className="text-sm font-medium"
            value={USERNAME}
            isDisabled={false}
            isSelected={!!selectedFilterBy?.userName}
            onChange={() => {
              setSelectedFilterBy({
                ...FILTERS_INIT_STATE,
                auto: false,

                firstName: false,
                userName: true,
              });
            }}
          />
          <RadioButton
            id="4"
            label="Phone Number"
            className="text-sm font-medium"
            value={PHONE_NUMBER}
            isSelected={!!selectedFilterBy?.phoneNumber}
            isDisabled={!!/^[a-zA-Z]+$/.test(searchQuery)}
            onChange={() => {
              setSelectedFilterBy({
                ...FILTERS_INIT_STATE,
                auto: false,
                firstName: false,
                phoneNumber: true,
              });
            }}
          />{" "}
          <RadioButton
            id="5"
            label="Id"
            className="text-sm font-medium"
            value={ID}
            isSelected={!!selectedFilterBy.id}
            isDisabled={!!/^[a-zA-Z]+$/.test(searchQuery)}
            onChange={() => {
              setSelectedFilterBy({
                ...FILTERS_INIT_STATE,
                auto: false,
                firstName: false,
                id: true,
              });
            }}
          />
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">Date of Birth</span>
            <div className="flex gap-2">
              <DatePicker
                value={dobFrom}
                onBlur={(dobFrom) => {
                  setDobFrom(formatDate(dobFrom, "MM-DD-YYYY"));
                }}
              />
              <DatePicker
                value={dobTo}
                onBlur={(dobTo) => {
                  setDobTo(formatDate(dobTo, "MM-DD-YYYY"));
                }}
              />
            </div>
          </div>
        </div>
      </fieldset>
      <div className="flex gap-2">
        <Button
          className="bg-white text-xs font-normal sapphire border border-gray-200 py-2 px-4 inline-flex rounded-md w-20 h-8 relative"
          btnText="Apply"
          isDisabled={false}
          onClick={() => {
            setApplyFilter((prevState) => !prevState);
            setFilterView((prevState) => !prevState);
          }}
        />
        <Button
          variant="transparent"
          className="bg-red text-xs font-normal sapphire border border-gray-200 py-2 px-4 inline-flex rounded-md w-20 h-8 relative"
          btnText="Cancel"
          isDisabled={false}
          onClick={() => {
            setDobTo("");
            setDobFrom("");
            setPageNumber(1);
            setFilterView((prevState) => !prevState);
            setSelectedFilterBy(FILTERS_INIT_STATE);
          }}
        />
      </div>
    </div>
  );
};
